import 'react-app-polyfill/stable';
import React from 'react';
import ReactDOM from 'react-dom';
import { processSilentRenew } from 'redux-oidc';
import { Provider } from 'react-redux';
import { isBlank } from '@trs/utils';
import userManager from './config/userManager/userManager';
import AppProvider from './AppProvider';
import configureStore from './config/store';
import GlobalStyle from './config/style';
import {
  getTenantFromUrl,
  enableWhyDidYouUpdate,
  configureBreakpoints,
  enableReactAxe,
} from './config/helpers';

const tenant = getTenantFromUrl();

const suppressResizeObserverRuntimeOverlay = () => {
  const resizeObserverErrors = [
    'ResizeObserver loop completed with undelivered notifications.',
    'ResizeObserver loop limit exceeded',
  ];

  const isResizeObserverError = (message) =>
    typeof message === 'string' &&
    resizeObserverErrors.some((resizeObserverError) => message.includes(resizeObserverError));

  // Wrap window.onerror — CRA dev overlay calls handleError through it, bypassing addEventListener
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    const errorMessage = (typeof message === 'string' && message) ||
      (error && error.message) ||
      '';

    if (isResizeObserverError(errorMessage)) {
      return true; // returning true prevents default browser error handling and overlays
    }
    return originalOnError ? originalOnError(message, source, lineno, colno, error) : false;
  };

  // capture: true runs before CRA's dev overlay bubble-phase listener
  window.addEventListener('error', (event) => {
    const message = event && (event.message || (event.error && event.error.message));

    if (isResizeObserverError(message)) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    const message = event && event.reason && event.reason.message;

    if (isResizeObserverError(message)) {
      event.preventDefault();
    }
  });

  // CRA's dev runtime overlay may also react to console.error messages.
  // Filter only known non-blocking ResizeObserver loop warnings in development.
  if (process.env.NODE_ENV === 'development') {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const message = args
        .map((arg) => {
          if (typeof arg === 'string') return arg;
          if (arg && typeof arg.message === 'string') return arg.message;
          return '';
        })
        .join(' ');

      if (isResizeObserverError(message)) {
        return;
      }

      originalConsoleError(...args);
    };
  }
};

if (window.location.pathname.includes('renew')) {
  processSilentRenew();
} else if (!isBlank(tenant)) {
  userManager.signinRedirect({ extraQueryParams: { domain_hint: tenant } });
} else {
  const store = configureStore();
  window.GLOBAL_STORE = store;
  suppressResizeObserverRuntimeOverlay();
  enableWhyDidYouUpdate();
  enableReactAxe();
  configureBreakpoints();

  ReactDOM.render(
    <Provider store={store}>
      <>
        <GlobalStyle />
        <AppProvider />
      </>
    </Provider>,
    document.getElementById('root')
  );
}
